import { useEffect, useState, useCallback } from 'react';
import { AlertTriangle, ExternalLink, ChevronDown, ChevronUp } from 'lucide-react';
import { api } from '../../api/client';
import { Can } from '../../lib/rbac';

interface CaseRow {
  id: number;
  ml_order_id: string;
  purchase_id: number;
  amazon_order_id: string;
  amount: string;
  bucket: string;
  status: string;
  detected_at: string;
  asin: string;
  title: string;
  amazon_status: string;
}

interface StuckRow {
  purchase_id: number;
  ml_order_id: string;
  asin: string;
  total_cost: string | null;
  stuck_since: string;
  title: string;
  actionable: boolean;
}

interface CasesData {
  total: number;
  counts: Record<string, number>;
  cases: CaseRow[];
  stuck?: StuckRow[];
}

const BUCKET_LABEL: Record<string, string> = {
  cancellable: 'Cancelar en Amazon YA',
  verify: 'Verificar estado real',
  in_transit: 'En viaje (rechazar/devolver)',
  delivered: 'Entregada (devolución)',
};

const BUCKET_STYLE: Record<string, string> = {
  cancellable: 'bg-red-100 text-red-800',
  verify: 'bg-amber-100 text-amber-800',
  in_transit: 'bg-orange-100 text-orange-800',
  delivered: 'bg-gray-200 text-gray-700',
};

export function CancelledCasesPanel({ sellerId }: { sellerId: number }) {
  const [data, setData] = useState<CasesData | null>(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get<CasesData>(`/amazon/cancelled-cases?seller_id=${sellerId}`);
      if (res.success && res.data) setData(res.data);
      else setData(null);
    } catch {
      setData(null); // panel informativo: sin toast
    }
  }, [sellerId]);

  useEffect(() => {
    load();
  }, [load]);

  const stuck = data?.stuck || [];
  if (!data || (data.total === 0 && stuck.length === 0)) return null;

  const markRequested = async (id: number) => {
    await api.post(`/amazon/cancelled-cases/${id}/cancel-requested`, { seller_id: sellerId });
    load();
  };

  return (
    <div className="rounded-lg border border-[var(--color-error)] bg-[var(--color-error-light)]">
      <button
        className="flex w-full flex-wrap items-center gap-2 px-4 py-3 text-left"
        onClick={() => setOpen(!open)}
      >
        <AlertTriangle className="h-5 w-5 text-[var(--color-error)]" />
        {data.total > 0 && (
          <span className="font-semibold text-[var(--color-error)]">
            Canceladas con compra en Amazon: {data.total}
          </span>
        )}
        {stuck.length > 0 && (
          <span className="rounded bg-violet-100 px-2 py-0.5 font-semibold text-violet-800">
            Compras trabadas sin concretar: {stuck.length}
          </span>
        )}
        <span className="text-sm text-[var(--color-text-secondary)]">
          {Object.entries(data.counts)
            .map(([b, n]) => `${BUCKET_LABEL[b] || b}: ${n}`)
            .join(' · ')}
        </span>
        <span className="ml-auto text-[var(--color-text-secondary)]">
          {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </span>
      </button>
      {open && (
        <div className="max-h-96 overflow-y-auto border-t border-[var(--color-border)] bg-[var(--color-surface)]">
          {stuck.map((s) => (
            <div
              key={`stuck-${s.purchase_id}`}
              className="flex items-center gap-3 border-b border-[var(--color-border)] px-4 py-2 text-sm text-[var(--color-text)]"
            >
              <span
                className={`shrink-0 rounded px-2 py-0.5 text-xs font-medium ${
                  s.actionable ? 'bg-violet-100 text-violet-800' : 'bg-fuchsia-100 text-fuchsia-800'
                }`}
              >
                {s.actionable ? 'Trabada sin concretar' : 'Trabada — revisar a mano'}
              </span>
              <span className="min-w-0 flex-1 truncate" title={s.title}>
                {s.title}
              </span>
              <span className="hidden font-mono text-xs text-[var(--color-text-secondary)] sm:inline">
                #{s.ml_order_id}
              </span>
              {s.total_cost != null && (
                <Can
                  cap="global.view_money"
                  fallback={<span className="shrink-0 text-[var(--color-text-muted)]">—</span>}
                >
                  <span className="shrink-0 font-semibold">${Number(s.total_cost).toFixed(2)}</span>
                </Can>
              )}
              <span className="shrink-0 text-xs text-[var(--color-text-secondary)]">
                {`trabada desde ${new Date(s.stuck_since).toLocaleDateString()} · ${
                  s.actionable
                    ? 'el vigilante la recompra solo'
                    : 'venta ya despachada o producto en depósito'
                }`}
              </span>
            </div>
          ))}
          {data.cases.map((c) => (
            <div
              key={c.id}
              className="flex items-center gap-3 border-b border-[var(--color-border)] px-4 py-2 text-sm text-[var(--color-text)]"
            >
              <span
                className={`shrink-0 rounded px-2 py-0.5 text-xs font-medium ${BUCKET_STYLE[c.bucket] || ''}`}
              >
                {BUCKET_LABEL[c.bucket] || c.bucket}
              </span>
              <span className="min-w-0 flex-1 truncate" title={c.title}>
                {c.title}
              </span>
              <span className="hidden font-mono text-xs text-[var(--color-text-secondary)] sm:inline">
                #{c.ml_order_id}
              </span>
              <Can
                cap="global.view_money"
                fallback={<span className="shrink-0 text-[var(--color-text-muted)]">—</span>}
              >
                <span className="shrink-0 font-semibold">${Number(c.amount).toFixed(2)}</span>
              </Can>
              <a
                href={`https://www.amazon.com/gp/your-account/order-details?orderID=${c.amazon_order_id}`}
                target="_blank"
                rel="noreferrer"
                className="flex shrink-0 items-center gap-1 rounded bg-amber-500 px-2 py-1 text-xs font-medium text-white hover:bg-amber-600"
              >
                <ExternalLink className="h-3 w-3" /> Abrir en Amazon
              </a>
              {c.status === 'open' ? (
                <Can cap="orders.cancel_purchase">
                  <button
                    onClick={() => markRequested(c.id)}
                    className="shrink-0 rounded border border-[var(--color-border)] px-2 py-1 text-xs hover:bg-[var(--color-surface-hover)]"
                  >
                    Pedí cancelación
                  </button>
                </Can>
              ) : (
                <span className="shrink-0 text-xs text-[var(--color-text-secondary)]">
                  en observación
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
